import React, { useContext, useEffect, useState } from "react";
import { Handle } from "react-flow-renderer";
import { Button, Form, Modal } from "react-bootstrap";
import { NodesContext } from "../provider/IvrContext";
import AudioPreviewModal from "../Helpers/AudioPreviewModel";
import { toast } from "react-toastify";
import {
    BACKEND_API_BASE_URL,
    VOICEMAIL_PROMPT_CATEGORIES,
    VOICEMAIL_MAX_DURATION,
    VOICEMAIL_MIN_DURATION,
    TRANSFER_DEFAULT_OPTIONS,
} from "../../../../../utilities/constant";
import { getApiCall } from "../../../../../service/httpCall";


const DefaultNode = ({ data, id }) => {
    const { updateNodeData } = useContext(NodesContext);

    const mode = data.mode || "";
    const action = data.action || {};
    const maxDuration = data.max_duration || "";

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [playModalOpen, setPlayModalOpen] = useState(false);
    const [promptList, setPromptList] = useState([]);
    const [errors, setErrors] = useState({});

    const [draftMode, setDraftMode] = useState(mode);
    const [draftAction, setDraftAction] = useState(action);
    const [draftCategory, setDraftCategory] = useState(action?.prompt_category || "");
    const [draftDuration, setDraftDuration] = useState(maxDuration);
    const [draftUseMedia, setDraftUseMedia] = useState(Boolean(data.use_media));

    useEffect(() => {
        const fetchPrompts = async () => {
            try {
                const resp = await getApiCall(
                    `${BACKEND_API_BASE_URL}/v2/prompt?prompt_status=1&user_id=${data.userId}`
                );
                if (!Array.isArray(resp)) return;
                setPromptList(resp.filter(p => VOICEMAIL_PROMPT_CATEGORIES.includes(p.prompt_category)));
            } catch (err) {
                console.error(err);
            }
        };
        if (data.userId) fetchPrompts();
    }, [data.userId]);

    const updateNode = (updates) => updateNodeData(id, updates);

    const openModal = () => {
        setDraftMode(mode);
        setDraftAction(action);
        setDraftCategory(action?.prompt_category || "");
        setDraftDuration(maxDuration);
        setDraftUseMedia(Boolean(data.use_media));
        setErrors({});
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setErrors({});
        setModalIsOpen(false);
    };

    const handleModeChange = (e) => {
        const m = e.target.value;
        setDraftMode(m);
        setDraftAction({});
        setDraftCategory("");
        setDraftDuration("");
        setDraftUseMedia(false);
    };

    const handleUseMediaToggle = (e) => {
        const checked = e.target.checked;
        setDraftUseMedia(checked);
        if (!checked) {
            setDraftCategory("");
            setDraftAction({});
        }
    };

    const handleCategoryChange = (e) => {
        const cat = e.target.value;
        setDraftCategory(cat);
        setDraftAction((prev) => ({ ...prev, prompt_category: cat, id: "", audio: "", name: "", duration: 0 }));
    };

    const handleAudioChange = (e) => {
        const selected = promptList.find((a) => a.id.toString() === e.target.value);
        if (!selected) {
            setDraftAction((prev) => ({ ...prev, id: "", audio: "", name: "", duration: 0 }));
            return;
        }
        setDraftAction({
            type: draftMode,
            prompt_category: draftCategory,
            id: selected.id,
            audio: selected.prompt_url,
            duration: selected.prompt_duration || 0,
            name: selected.prompt_name,
        });
    };

    const handleDurationChange = (e) => {
        const val = e.target.value;
        if (/^\d*$/.test(val) && (val === "" || Number(val) <= VOICEMAIL_MAX_DURATION)) {
            setDraftDuration(val);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!draftMode) newErrors.mode = "Action is required";
        if (draftMode === "hangup_with_media") {
            if (draftCategory && !draftAction?.id) {
                newErrors.audio = "Audio is required when category is selected";
            }
        }
        if (draftMode === "voicemail") {
            if (draftUseMedia && draftCategory && !draftAction?.id) {
                newErrors.audio = "Audio is required when category is selected";
            }
            if (!draftDuration || Number(draftDuration) < VOICEMAIL_MIN_DURATION) {
                newErrors.duration = `Min ${VOICEMAIL_MIN_DURATION}s`;
            } else if (Number(draftDuration) > VOICEMAIL_MAX_DURATION) {
                newErrors.duration = `Max ${VOICEMAIL_MAX_DURATION}s`;
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        const updates = { mode: draftMode };
        if (draftMode === "hangup") {
            updates.action = {};
            updates.max_duration = null;
            updates.use_media = false;
        } else if (draftMode === "voicemail") {
            updates.action = draftUseMedia ? draftAction : {};
            updates.max_duration = Number(draftDuration);
            updates.use_media = draftUseMedia;
        } else {
            updates.action = draftAction;
            updates.max_duration = null;
            updates.use_media = false;
        }
        updateNode(updates);
        toast.success("Default action updated.");
        setModalIsOpen(false);
    };

    const filteredAudio = promptList.filter(
        (p) => !draftCategory || p.prompt_category === draftCategory
    );
    const audioUrl = action?.audio || "";
    const audioName = action?.name || "";
    const modeLabel =
        TRANSFER_DEFAULT_OPTIONS.find((m) => m.value === mode)?.label || "Default";

    return (
        <>
            <div
                className="ivr-flow-node"
                style={{
                    width: "100px",
                    border: "2px solid #ffc107",
                    borderRadius: "12px",
                    background: "#fffbea",
                    position: "relative",
                    display: "flex",
                    padding: "6px",
                    flexDirection: "column",
                    alignItems: "center",
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Handle
                    type="target"
                    position="top"
                    id="default-target"
                    style={{
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#ffc107",
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                    }}
                    isConnectable={false}
                />

                <span
                    className="ivr-flow-node-label"
                    style={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        color: "#333",
                        textAlign: "center",
                        textTransform: "capitalize",
                    }}
                >
                    Default: {modeLabel}
                </span>

                <div style={{ display: "flex", gap: "8px", marginTop: 4 }}>
                    <i
                        className="fa fa-cog ivr-flow-node-icon"
                        style={{ color: "#ffc107", fontSize: "12px", cursor: "pointer" }}
                        title="Settings"
                        onClick={openModal}
                    />
                    {audioUrl && (
                        <i
                            className="fa fa-play ivr-flow-node-icon"
                            style={{ color: "#28a745", fontSize: "12px", cursor: "pointer" }}
                            title="Play audio"
                            onClick={() => setPlayModalOpen(true)}
                        />
                    )}
                </div>
            </div>

            <Modal size="lg" show={modalIsOpen} onHide={closeModal} backdrop={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Default Action (Uncovered Time)</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Action<Form.Text className="text-danger">*</Form.Text></Form.Label>
                            <Form.Control as="select" value={draftMode} onChange={handleModeChange}>
                                <option value="">Select Action</option>
                                {TRANSFER_DEFAULT_OPTIONS.map((m) => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </Form.Control>
                            {errors.mode && <Form.Text className="text-danger">{errors.mode}</Form.Text>}
                        </Form.Group>

                        {draftMode === "hangup_with_media" && (
                            <>
                                <Form.Group style={{ marginTop: 15 }}>
                                    <Form.Label>Prompt Category</Form.Label>
                                    <Form.Control as="select" value={draftCategory} onChange={handleCategoryChange}>
                                        <option value="">Select Category</option>
                                        {VOICEMAIL_PROMPT_CATEGORIES.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </Form.Control>
                                    {errors.category && <Form.Text className="text-danger">{errors.category}</Form.Text>}
                                </Form.Group>

                                <Form.Group style={{ marginTop: 15 }}>
                                    <Form.Label>Select Audio</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={draftAction?.id || ""}
                                        onChange={handleAudioChange}
                                        disabled={!draftCategory}
                                    >
                                        <option value="">Select Audio</option>
                                        {filteredAudio.map((item) => (
                                            <option key={item.id} value={item.id}>{item.prompt_name}</option>
                                        ))}
                                    </Form.Control>
                                    {errors.audio && <Form.Text className="text-danger">{errors.audio}</Form.Text>}
                                </Form.Group>
                            </>
                        )}

                        {draftMode === "voicemail" && (
                            <>
                                <Form.Group style={{ marginTop: 15 }}>
                                    <Form.Label>Max Voicemail Duration (sec, {VOICEMAIL_MIN_DURATION}-{VOICEMAIL_MAX_DURATION})<Form.Text className="text-danger">*</Form.Text></Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={VOICEMAIL_MIN_DURATION}
                                        max={VOICEMAIL_MAX_DURATION}
                                        value={draftDuration}
                                        onChange={handleDurationChange}
                                    />
                                    {errors.duration && <Form.Text className="text-danger">{errors.duration}</Form.Text>}
                                </Form.Group>

                                <Form.Group style={{ marginTop: 15 }}>
                                    <Form.Check
                                        type="checkbox"
                                        id={`def-use-media-${id}`}
                                        label="Use Media"
                                        checked={draftUseMedia}
                                        onChange={handleUseMediaToggle}
                                    />
                                </Form.Group>

                                {draftUseMedia && (
                                    <>
                                        <Form.Group style={{ marginTop: 15 }}>
                                            <Form.Label>Prompt Category</Form.Label>
                                            <Form.Control as="select" value={draftCategory} onChange={handleCategoryChange}>
                                                <option value="">Select Category</option>
                                                {VOICEMAIL_PROMPT_CATEGORIES.map((c) => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>

                                        <Form.Group style={{ marginTop: 15 }}>
                                            <Form.Label>Select Audio</Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={draftAction?.id || ""}
                                                onChange={handleAudioChange}
                                                disabled={!draftCategory}
                                            >
                                                <option value="">Select Audio</option>
                                                {filteredAudio.map((item) => (
                                                    <option key={item.id} value={item.id}>{item.prompt_name}</option>
                                                ))}
                                            </Form.Control>
                                            {errors.audio && <Form.Text className="text-danger">{errors.audio}</Form.Text>}
                                        </Form.Group>
                                    </>
                                )}
                            </>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave}>Save</Button>
                    <Button variant="secondary" onClick={closeModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            <AudioPreviewModal
                show={playModalOpen}
                onClose={() => setPlayModalOpen(false)}
                audioUrl={audioUrl}
                title={audioName}
            />
        </>
    );
};

export default DefaultNode;
